'use client';

import React, { useState, useEffect } from 'react';
import ReactPhoneNumberInput, { Country } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { cn } from '@/lib/utils';

interface PhoneInputProps {
  value: string;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  error?: boolean;
}

export function PhoneInput({
  value,
  onChange,
  disabled = false,
  required = false,
  autoFocus = false,
  placeholder = 'Enter phone number',
  className,
  error = false,
}: PhoneInputProps) {
  const [defaultCountry, setDefaultCountry] = useState<Country>('US');

  useEffect(() => {
    // Auto-detect user's country from timezone or fetch from geolocation API
    const detectCountry = async () => {
      try {
        // Try to detect from timezone first (fast, no API call)
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Map common timezones to countries
        const timezoneCountryMap: Record<string, Country> = {
          // United States
          'America/New_York': 'US',
          'America/Chicago': 'US',
          'America/Denver': 'US',
          'America/Phoenix': 'US',
          'America/Los_Angeles': 'US',
          'America/Anchorage': 'US',
          'America/Honolulu': 'US',
          // Canada
          'America/Toronto': 'CA',
          'America/Vancouver': 'CA',
          'America/Edmonton': 'CA',
          'America/Winnipeg': 'CA',
          'America/Halifax': 'CA',
          'America/St_Johns': 'CA',
          'America/Regina': 'CA',
          'America/Whitehorse': 'CA',
          'America/Yellowknife': 'CA',
          'America/Iqaluit': 'CA',
          'America/Montreal': 'CA',
          // Other countries
          'America/Mexico_City': 'MX',
          'Europe/London': 'GB',
          'Europe/Paris': 'FR',
          'Europe/Berlin': 'DE',
          'Asia/Tokyo': 'JP',
          'Asia/Shanghai': 'CN',
          'Asia/Hong_Kong': 'HK',
          'Asia/Singapore': 'SG',
          'Australia/Sydney': 'AU',
        };

        const detectedCountry = timezoneCountryMap[timezone];
        console.log('[PhoneInput] User timezone:', timezone);
        console.log('[PhoneInput] Mapped country:', detectedCountry);

        if (detectedCountry) {
          console.log('[PhoneInput] Setting country from timezone:', detectedCountry);
          setDefaultCountry(detectedCountry);
          return;
        }

        // Fallback: Use free IP geolocation API
        const response = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(3000)
        });

        if (response.ok) {
          const data = await response.json();
          if (data.country_code) {
            console.log('[PhoneInput] Detected country from IP:', data.country_code);
            setDefaultCountry(data.country_code as Country);
          }
        }
      } catch (error) {
        console.log('[PhoneInput] Could not detect country, using US as default');
        // Keep default as US if detection fails
      }
    };

    detectCountry();
  }, []);

  return (
    <ReactPhoneNumberInput
      international
      defaultCountry={defaultCountry}
      value={value}
      onChange={onChange}
      disabled={disabled}
      required={required}
      autoFocus={autoFocus}
      placeholder={placeholder}
      className={cn(
        'phone-input-container',
        className
      )}
      numberInputProps={{
        className: cn(
          'w-full px-3 py-2 border rounded-md shadow-sm',
          'text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800',
          'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500',
          error
            ? 'border-red-500 dark:border-red-600'
            : 'border-gray-300 dark:border-gray-600',
          disabled && 'opacity-50 cursor-not-allowed'
        )
      }}
      countrySelectProps={{
        className: cn(
          'px-2 py-2 border rounded-l-md',
          'text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800',
          'border-gray-300 dark:border-gray-600',
          'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500',
          disabled && 'opacity-50 cursor-not-allowed'
        )
      }}
    />
  );
}
